import { useEffect, useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  Users, 
  Users2,
  ChevronLeft,
  ChevronRight,
  Rocket,
  Trophy,
  Calendar,
  CalendarDays,
  Wand2,
  FileText,
  Receipt,
  Settings,
  LogOut,
  MessageCircle,
  Menu,
  X
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { FeedbackDialog } from "./FeedbackDialog";
import axios from "axios";
import { clearToken } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Layout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Auto-close drawer on route change (so nav-item click closes it)
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll when drawer is open on mobile
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const navItems = [
    { to: "/get-started", icon: Rocket, label: "Aan de slag" },
    { to: "/overview", icon: FileText, label: "Overzicht" },
    { to: "/planning", icon: Wand2, label: "Planning" },
    { to: "/matches", icon: Calendar, label: "Wedstrijden" },
    { to: "/activities", icon: CalendarDays, label: "Activiteiten" },
    { to: "/players", icon: Users, label: "Spelers" },
    { to: "/expenses", icon: Receipt, label: "Teamuitgaven" },
    { to: "/users", icon: Users2, label: "Gebruikers" },
    { to: "/season", icon: Settings, label: "Seizoen" },
  ];

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex min-h-screen bg-slate-50">
        {/* Mobile header — sticky, hamburger + brand + close/logout, visible only < md */}
        <header
          className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b border-slate-200 flex items-center justify-between px-3 h-14 safe-header"
          data-testid="mobile-header"
        >
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="h-10 w-10 flex items-center justify-center rounded-md hover:bg-slate-100"
            aria-label="Open menu"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5 text-slate-700" />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <img
              src="/favicon.svg"
              alt="Sportly"
              className="w-8 h-8 rounded-md flex-shrink-0"
            />
            <span
              className="font-semibold text-slate-900 text-sm truncate"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              Sportly
            </span>
          </div>
          <div className="w-10" />
        </header>

        {/* Backdrop — visible only when drawer open on mobile */}
        {mobileOpen && (
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="lg:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
            aria-label="Sluit menu"
            data-testid="mobile-backdrop"
          />
        )}

        {/* Sidebar */}
        <aside 
          className={cn(
            "sidebar bg-white border-r border-slate-200 flex flex-col fixed h-full z-50",
            // Desktop: always visible, width toggled by collapse
            "lg:z-20 lg:translate-x-0",
            collapsed ? "sidebar-collapsed" : "sidebar-expanded",
            // Mobile: off-canvas by default, slide in when mobileOpen
            mobileOpen ? "translate-x-0" : "-translate-x-full",
          )}
          data-testid="sidebar"
        >
          {/* Logo */}
          <div className={cn(
            "h-16 flex items-center border-b border-slate-100",
            collapsed ? "lg:justify-center lg:px-2 justify-between px-4" : "justify-between px-4"
          )}>
            {collapsed ? (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setCollapsed(false)}
                  className="h-10 w-10 hidden lg:inline-flex"
                  data-testid="sidebar-toggle"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
                {/* Mobile: brand + close in collapsed-desktop but drawer is open */}
                <div className="lg:hidden flex items-center gap-2 overflow-hidden">
                  <img
                    src="/favicon.svg"
                    alt="Sportly"
                    className="w-9 h-9 rounded-lg flex-shrink-0"
                  />
                  <span className="font-semibold text-slate-900 whitespace-nowrap" style={{ fontFamily: 'var(--font-display)' }}>
                    Sportly Hockey Planner
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setMobileOpen(false)}
                  className="h-8 w-8 flex-shrink-0 lg:hidden"
                  aria-label="Sluit menu"
                  data-testid="mobile-close-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 overflow-hidden">
                  <img
                    src="/favicon.svg"
                    alt="Sportly"
                    className="w-9 h-9 rounded-lg flex-shrink-0"
                  />
                  <span className="font-semibold text-slate-900 whitespace-nowrap" style={{ fontFamily: 'var(--font-display)' }}>
                    Sportly Hockey Planner
                  </span>
                </div>
                {/* Desktop collapse toggle */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setCollapsed(true)}
                  className="h-8 w-8 flex-shrink-0 hidden lg:inline-flex"
                  data-testid="sidebar-toggle"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                {/* Mobile close button */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setMobileOpen(false)}
                  className="h-8 w-8 flex-shrink-0 lg:hidden"
                  aria-label="Sluit menu"
                  data-testid="mobile-close-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.to;
              const NavContent = (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={cn("nav-item", isActive && "active")}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="nav-item-icon" />
                  {!collapsed && <span className="nav-item-label">{item.label}</span>}
                </NavLink>
              );

              if (collapsed) {
                return (
                  <Tooltip key={item.to}>
                    <TooltipTrigger asChild>
                      {NavContent}
                    </TooltipTrigger>
                    <TooltipContent side="right" sideOffset={10}>
                      {item.label}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return NavContent;
            })}
          </nav>

          {/* Feedback */}
          <div className="px-3 pb-1">
            {collapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setShowFeedback(true)}
                    className="nav-item w-full text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                    data-testid="feedback-link"
                  >
                    <MessageCircle className="nav-item-icon" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={10}>Fout of idee melden?</TooltipContent>
              </Tooltip>
            ) : (
              <button
                onClick={() => setShowFeedback(true)}
                className="nav-item w-full text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                data-testid="feedback-link"
              >
                <MessageCircle className="nav-item-icon" />
                <span className="nav-item-label text-xs">Fout of idee melden?</span>
              </button>
            )}
          </div>

          {/* Logout */}
          <div className="p-3 border-t border-slate-100 safe-bottom" data-testid="logout-container">
            {collapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={async () => { await axios.post(`${API}/auth/logout`).catch(() => {}); clearToken(); navigate("/login", { replace: true }); }}
                    className="nav-item w-full text-slate-500 hover:text-red-600 hover:bg-red-50"
                    data-testid="logout-btn"
                  >
                    <LogOut className="nav-item-icon" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={10}>Uitloggen</TooltipContent>
              </Tooltip>
            ) : (
              <button
                onClick={async () => { await axios.post(`${API}/auth/logout`).catch(() => {}); clearToken(); navigate("/login", { replace: true }); }}
                className="nav-item w-full text-slate-500 hover:text-red-600 hover:bg-red-50"
                data-testid="logout-btn"
              >
                <LogOut className="nav-item-icon" />
                <span className="nav-item-label">Uitloggen</span>
              </button>
            )}
          </div>
        </aside>

        {/* Main Content */}
        <main 
          className={cn(
            "flex-1 min-w-0 overflow-hidden transition-all duration-300 safe-main pt-mobile-header",
            // Desktop margins mirror sidebar width; mobile has no margin (drawer overlays)
            collapsed ? "lg:main-collapsed" : "lg:main-expanded",
          )}
        >
          <div className="p-6 md:p-8 h-full overflow-y-auto">
            <Outlet />
          </div>
        </main>
      </div>
      <FeedbackDialog open={showFeedback} onOpenChange={setShowFeedback} />
    </TooltipProvider>
  );
};
